import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ApiError, apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { getShMeta } from '../../api/shMeta.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface ReviewDetail {
  readonly reviewId: string;
  readonly tenantId: string;
  /** The review has no number of its own; it is known by its job's number. */
  readonly jobNumber: string | null;
  readonly companyApprovalId: string;
  readonly status: string;
  readonly reviewerUserId: string | null;
  readonly reviewerRoleCode: string | null;
  readonly priorityCode: string | null;
  readonly slaDeadlineAt: string | null;
  readonly estimatedCostInr: number | null;
  readonly approvedCostInr: number | null;
  readonly actualCostInr: number | null;
  readonly variancePct: number | null;
  readonly varianceTierCode: string | null;
  readonly isHighValue: boolean;
  readonly cycleNo: number;
  readonly decisionCode: string | null;
  readonly decisionRemarks: string | null;
  readonly decisionAt: string | null;
  readonly financeEligibleAt: string | null;
  readonly territoryCode: string | null;
  readonly createdAt: string;
}

interface CostValidation {
  readonly validationId: string;
  readonly estimatedCostInr: number;
  readonly approvedCostInr: number;
  readonly actualCostInr: number;
  readonly varianceAmountInr: number;
  readonly variancePct: number;
  readonly varianceTierCode: string;
  readonly validatedAt: string;
}

interface ApprovalRow {
  readonly approvalId: string;
  readonly approverUserId: string;
  readonly approvalLevelCode: string;
  readonly decisionCode: string;
  readonly decisionRemarks: string;
  readonly approvedCostInr: number | null;
  readonly delegatedFromUserId: string | null;
  readonly createdAt: string;
}

interface RoiAssessment {
  readonly assessmentId: string;
  readonly overallRoiScore: number;
  readonly recommendationCode: string;
  readonly assessmentRemarks: string | null;
}

const CARD: React.CSSProperties = {
  padding: 16, borderRadius: 8, marginBottom: 16,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
};

const LABEL: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 2 };
const VAL: React.CSSProperties = { fontSize: 14, fontWeight: 500, marginBottom: 12 };


export function ShReviewDetailPage(): ReactNode {
  const { reviewId } = useParams<{ reviewId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money, number } = useBusinessNumber();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();

  const [data, setData] = useState<ReviewDetail | null>(null);
  const [costVal, setCostVal] = useState<CostValidation | null>(null);
  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [roi, setRoi] = useState<RoiAssessment | null>(null);
  const [decisionCodes, setDecisionCodes] = useState<readonly string[]>([]);
  // Kept apart from `error`, which the review reload clears: without the meta
  // there is no governed decision code to send, and that must stay said.
  const [metaError, setMetaError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Decision form — the code is one the tenant governs, never a default here.
  const [decisionCode, setDecisionCode] = useState<string>('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    let alive = true;
    getShMeta(token)
      .then((m) => {
        if (!alive) return;
        setDecisionCodes(m.decisionCodes);
        setDecisionCode((prev) => (m.decisionCodes.includes(prev) ? prev : m.decisionCodes[0] ?? ''));
      })
      .catch((e) => { if (alive) setMetaError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  const load = useCallback(async () => {
    if (!reviewId) return;
    try {
      const [d, cv, ap] = await Promise.all([
        apiRequest<ReviewDetail>(`/v1/sh/review/${encodeURIComponent(reviewId)}`, { token }),
        // Both answer under the review's own permission, and the approver
        // decides on them — an unread one is a failure, not "none".
        apiRequest<CostValidation | null>(`/v1/sh/review/${encodeURIComponent(reviewId)}/cost-validation`, { token }),
        apiRequest<ApprovalRow[]>(`/v1/sh/review/${encodeURIComponent(reviewId)}/approvals`, { token }),
      ]);
      setData(d);
      setCostVal(cv);
      setApprovals(ap);
      setError(null);

      // ROI needs ROI_VIEW: without it (403) or without an assessment (404)
      // the section is left out; any other failure is said.
      apiRequest<RoiAssessment>(`/v1/sh/review/${encodeURIComponent(reviewId)}/roi`, { token })
        .then(setRoi)
        .catch((e) => {
          setRoi(null);
          if (!(e instanceof ApiError && (e.status === 403 || e.status === 404))) setError((e as Error).message);
        });
    } catch (e) {
      setError((e as Error).message);
    }
  }, [reviewId, token]);

  useEffect(() => { void load(); }, [load]);

  const handleDecide = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!reviewId || busy || metaError || !decisionCode) return;
    if (remarks.length < 10) { setError(t('sh.common.remarksMin10')); return; }
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/sh/review/${encodeURIComponent(reviewId)}/decide`, {
        method: 'POST', token, body: { decisionCode, decisionRemarks: remarks },
      });
      setInfo(t('sh.review.decisionRecorded', { code: decisionCode }));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, decisionCode, remarks, token, busy, load, metaError]);

  const handleStart = useCallback(async () => {
    if (!reviewId || busy) return;
    setBusy(true); setError(null);
    try {
      await apiRequest(`/v1/sh/review/${encodeURIComponent(reviewId)}/start`, { method: 'POST', token });
      setInfo(t('sh.review.started'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, token, busy, load]);

  const handleFinanceEligible = useCallback(async () => {
    if (!reviewId || busy) return;
    setBusy(true); setError(null);
    try {
      await apiRequest(`/v1/sh/review/${encodeURIComponent(reviewId)}/finance-eligible`, { method: 'POST', token });
      setInfo(t('sh.review.financeEligibleMsg'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [reviewId, token, busy, load]);

  if (!data && !error) return <main aria-label="sh-review-detail"><p>{t('sh.common.loading')}</p></main>;

  return (
    <main aria-label="sh-review-detail">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/sh/dashboard" data-testid="back-dashboard">{t('sh.common.dashboard')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.review.title', { job: data?.jobNumber ?? '—' })}</h1>
      </div>

      {metaError && <p role="alert" data-testid="sh-meta-error" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{metaError}</p>}
      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {data && (
        <>
          {/* ── Summary ──────────────────────────────── */}
          <section style={CARD} aria-label="review-summary">
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('sh.review.summary')}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              <div><div style={LABEL}>{t('sh.review.jobRequest')}</div><div style={VAL}>{data.jobNumber ?? '—'}</div></div>
              <div><div style={LABEL}>{t('sh.common.status')}</div><div style={VAL}>{data.status}</div></div>
              <div><div style={LABEL}>{t('sh.common.priority')}</div><div style={VAL}>{data.priorityCode ?? '—'}</div></div>
              <div><div style={LABEL}>{t('sh.review.estimatedCost')}</div><div style={VAL}>₹{money(data.estimatedCostInr)}</div></div>
              <div><div style={LABEL}>{t('sh.review.approvedCost')}</div><div style={VAL}>₹{money(data.approvedCostInr)}</div></div>
              <div><div style={LABEL}>{t('sh.review.actualCost')}</div><div style={VAL}>₹{money(data.actualCostInr)}</div></div>
              <div><div style={LABEL}>{t('sh.review.variance')}</div><div style={VAL}>{data.variancePct != null ? `${number(data.variancePct)}%` : '—'} ({data.varianceTierCode ?? '—'})</div></div>
              <div><div style={LABEL}>{t('sh.review.highValue')}</div><div style={VAL}>{data.isHighValue ? t('sh.common.yes') : t('sh.common.no')}</div></div>
              <div><div style={LABEL}>{t('sh.common.cycle')}</div><div style={VAL}>{data.cycleNo}</div></div>
              <div><div style={LABEL}>{t('sh.review.territory')}</div><div style={VAL}>{data.territoryCode ?? '—'}</div></div>
              <div><div style={LABEL}>{t('sh.review.slaDeadline')}</div><div style={VAL}>{data.slaDeadlineAt ? at(data.slaDeadlineAt) : '—'}</div></div>
              <div><div style={LABEL}>{t('sh.review.created')}</div><div style={VAL}>{at(data.createdAt)}</div></div>
              {data.decisionCode && <div><div style={LABEL}>{t('sh.common.decision')}</div><div style={VAL}>{data.decisionCode}</div></div>}
              {data.decisionRemarks && <div><div style={LABEL}>{t('sh.review.decisionRemarks')}</div><div style={VAL}>{data.decisionRemarks}</div></div>}
              {data.financeEligibleAt && <div><div style={LABEL}>{t('sh.review.financeEligibleAt')}</div><div style={VAL}>{at(data.financeEligibleAt)}</div></div>}
            </div>
          </section>

          {/* ── Cost Validation ──────────────────────── */}
          {costVal && (
            <section style={CARD} aria-label="cost-validation">
              <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('sh.review.costValidation')}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                <div><div style={LABEL}>{t('sh.review.estimated')}</div><div style={VAL}>₹{money(costVal.estimatedCostInr)}</div></div>
                <div><div style={LABEL}>{t('sh.review.approved')}</div><div style={VAL}>₹{money(costVal.approvedCostInr)}</div></div>
                <div><div style={LABEL}>{t('sh.review.actual')}</div><div style={VAL}>₹{money(costVal.actualCostInr)}</div></div>
                <div><div style={LABEL}>{t('sh.review.variance')}</div><div style={VAL}>{number(costVal.variancePct)}% (₹{money(costVal.varianceAmountInr)})</div></div>
                <div><div style={LABEL}>{t('sh.review.tier')}</div><div style={VAL}>{costVal.varianceTierCode}</div></div>
                <div><div style={LABEL}>{t('sh.review.validated')}</div><div style={VAL}>{at(costVal.validatedAt)}</div></div>
              </div>
            </section>
          )}

          {/* ── ROI ──────────────────────────────────── */}
          {roi && (
            <section style={CARD} aria-label="roi-assessment">
              <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('sh.review.roiAssessment')}</h2>
              <div><strong>{t('sh.review.overallScore')}</strong> {number(roi.overallRoiScore)}/10</div>
              <div><strong>{t('sh.review.recommendation')}</strong> {roi.recommendationCode}</div>
              {roi.assessmentRemarks && <div><strong>{t('sh.review.remarksLabel')}</strong> {roi.assessmentRemarks}</div>}
            </section>
          )}

          {/* ── Approval History ──────────────────────── */}
          {approvals.length > 0 && (
            <section style={CARD} aria-label="approval-history">
              <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('sh.review.approvalHistory')}</h2>
              <DataTable<ApprovalRow>
                rows={approvals}
                rowKey={(a) => a.approvalId}
                columns={[
                  { key: 'level', header: t('sh.common.level'), render: (a) => <>{a.approvalLevelCode}{a.delegatedFromUserId ? t('sh.review.delegatedSuffix') : ''}</> },
                  { key: 'decision', header: t('sh.common.decision'), render: (a) => a.decisionCode },
                  { key: 'cost', header: t('sh.review.colCost'), render: (a) => <>₹{money(a.approvedCostInr)}</> },
                  { key: 'remarks', header: t('sh.common.remarks'), render: (a) => a.decisionRemarks },
                  { key: 'date', header: t('sh.common.date'), render: (a) => at(a.createdAt) },
                ]}
              />
            </section>
          )}

          {/* ── Actions ──────────────────────────────── */}
          <section style={CARD} aria-label="review-actions">
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>{t('sh.review.actions')}</h2>

            {data.status === 'PENDING_REVIEW' && (
              <Button type="button" variant="secondary" onClick={handleStart} disabled={busy}
                      data-testid="btn-start-review"
                      style={{ marginRight: 8 }}>
                {t('sh.review.startReview')}
              </Button>
            )}

            {data.status === 'APPROVED' && !data.financeEligibleAt && (
              <Button type="button" variant="secondary" onClick={handleFinanceEligible} disabled={busy}
                      data-testid="btn-finance-eligible"
                      style={{ marginRight: 8 }}>
                {t('sh.review.markFinanceEligible')}
              </Button>
            )}

            {['IN_REVIEW', 'CLARIFICATION_REQUIRED', 'ON_HOLD'].includes(data.status) && (
              <form onSubmit={handleDecide} aria-label="decide-form" style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label htmlFor="sh-decision" style={LABEL}>{t('sh.common.decision')}</label>
                    <select id="sh-decision" value={decisionCode} onChange={(e) => setDecisionCode(e.target.value)}
                            data-testid="select-decision"
                            style={{ padding: '8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }}>
                      {decisionCodes.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label htmlFor="sh-remarks" style={LABEL}>{t('sh.common.remarksLabel10')}</label>
                    <textarea id="sh-remarks" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
                              data-testid="input-remarks"
                              style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid var(--color-border-default)', resize: 'vertical' }} />
                  </div>
                  <Button type="submit" variant="primary" disabled={busy || !!metaError || !decisionCode} data-testid="btn-decide">
                    {t('sh.review.submitDecision')}
                  </Button>
                </div>
              </form>
            )}

            <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
              <Link to={`/sh/review/${reviewId}/cost`} data-testid="link-cost-analysis">{t('sh.review.linkCostAnalysis')}</Link>
              <Link to={`/sh/review/${reviewId}/clarification`} data-testid="link-clarification">{t('sh.review.linkClarifications')}</Link>
              <Link to={`/sh/review/${reviewId}/escalation`} data-testid="link-escalation">{t('sh.review.linkEscalations')}</Link>
              <Link to={`/sh/review/${reviewId}/roi`} data-testid="link-roi">{t('sh.review.linkRoi')}</Link>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
